import { type ReviewStage } from '@maxus/common'
import {
  bridge,
  queryClient,
  toBridgeSyncComments,
  trpc,
  trpcClient,
} from '@maxus/website/lib'

import { RenderCanceledError, renderManager } from '../../../../contexts/render-manager'

const MASTER_MEDIA_POOL_PATH = '15-Maxus'

type ModificationManagerInput = {
  masters: {
    platformIds: string[]
    platformNames?: string[]
  }[]
  renderAssetsAfterMaster?: {
    deliverableId: string
  }
  note?: string
  sourceTimelineVersionId: string
  sourceTimelineVersionAssetId?: string
  sourceStageName: string
  stageName: ReviewStage
  renderMaster: boolean
  syncCommentsEnabled: boolean
}

type ModificationManagerEvent =
  | { type: 'canceled'; sourceTimelineVersionId: string }
  | { type: 'created'; sourceTimelineVersionId: string }
  | { type: 'failed'; description?: string; sourceTimelineVersionId: string }
  | { type: 'master-render-complete'; sourceTimelineVersionId: string }
  | { type: 'rendering-master'; sourceTimelineVersionId: string }

type ModificationManagerListener = (event: ModificationManagerEvent) => void

export class ModificationManager {
  private listeners = new Set<ModificationManagerListener>()
  private runningTimelineVersionIds = new Set<string>()

  isRunning(timelineVersionId: string) {
    return this.runningTimelineVersionIds.has(timelineVersionId)
  }

  start(input: ModificationManagerInput): boolean {
    const sourceTimelineVersionId = input.sourceTimelineVersionId
    if (this.runningTimelineVersionIds.has(sourceTimelineVersionId)) return false

    this.runningTimelineVersionIds.add(sourceTimelineVersionId)
    void this
      .run(input)
      .catch(error => {
        this.emit({
          type: 'failed',
          description: error instanceof Error ? error.message : undefined,
          sourceTimelineVersionId,
        })
      })
      .finally(() => {
        this.runningTimelineVersionIds.delete(sourceTimelineVersionId)
      })
    return true
  }

  subscribe(listener: ModificationManagerListener) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(event: ModificationManagerEvent) {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (error) {
        console.error('[modification-manager] listener failed', error)
      }
    }
  }

  private async run(input: ModificationManagerInput) {
    if (input.renderMaster && input.stageName === 'Distribution' && input.sourceStageName !== 'Distribution') {
      await this.runMasterCreation(input)
      return
    }

    await this.runTimelineVersionCreation(input)
  }

  private async runMasterCreation({
    masters,
    renderAssetsAfterMaster,
    note,
    sourceTimelineVersionId,
    sourceTimelineVersionAssetId,
    syncCommentsEnabled,
  }: ModificationManagerInput) {
    const result = await trpcClient.project.masterRender.create.mutate({
      masters: masters.map(({ platformIds }) => ({ platformIds })),
      note,
      sourceTimelineVersionId,
    })
    await queryClient.invalidateQueries({
      queryKey: trpc.project.deliverables.get.queryKey(),
    })
    const completionSource = { sourceTimelineVersionId: result.sourceTimelineVersionId }

    this.emit({ type: 'rendering-master', sourceTimelineVersionId })
    const assetRenders: {
      platformNames: string[]
      timelineName: string
      timelineVersionId: string
    }[] = []
    let renderOutcome: Awaited<ReturnType<typeof renderManager.runWithPostRender>>
    try {
      renderOutcome = await renderManager.runWithPostRender(
        result.masterRender,
        async (renderResult, { isCanceled }) => {
          const assertNotCanceled = () => {
            if (isCanceled()) throw new RenderCanceledError()
          }

          assertNotCanceled()
          this.emit({ type: 'master-render-complete', sourceTimelineVersionId })
          const bridgeComments =
            syncCommentsEnabled && sourceTimelineVersionAssetId
              ? toBridgeSyncComments(
                  (
                    await trpcClient.project.comments.list.query({
                      timelineVersionAssetId: sourceTimelineVersionAssetId,
                      allComments: true,
                    })
                  ).items,
                )
              : null
          assertNotCanceled()
          const timelineErrors: Error[] = []

          for (const [index, modification] of result.modifications.entries()) {
            try {
              assertNotCanceled()
              await bridge.createTimelineUsingClip({
                project_name: result.projectName,
                project_manager_path: result.masterRender.projectManagerPath,
                new_timeline_name: modification.newTimelineName,
                clip_file_path: renderResult.clipFilePath,
                media_pool_path: MASTER_MEDIA_POOL_PATH,
              })
              assertNotCanceled()
              if (bridgeComments) {
                await bridge.syncComments({
                  project_name: result.projectName,
                  project_manager_path: result.masterRender.projectManagerPath,
                  timeline_name: modification.newTimelineName,
                  comment_list: bridgeComments,
                  sync_mode: 'replace',
                })
              }
              assertNotCanceled()
              // 名称只在建线与评论同步均成功后物化，供 masterRender.finish 校验完整性。
              await trpcClient.project.timelineVersion.updateName.mutate({
                timelineVersionId: modification.newTimelineVersionId,
                ...completionSource,
              })
              if (renderAssetsAfterMaster) {
                const platformNames = masters[index]?.platformNames
                if (!platformNames?.length) {
                  throw new Error('Master platforms are not available')
                }
                assetRenders.push({
                  platformNames,
                  timelineName: modification.newTimelineName,
                  timelineVersionId: modification.newTimelineVersionId,
                })
              }
              assertNotCanceled()
            } catch (error) {
              timelineErrors.push(error instanceof Error ? error : new Error(String(error)))
            }
          }

          if (timelineErrors.length > 0) throw timelineErrors[0]
        },
      )
    } catch (error) {
      await trpcClient.project.masterRender.finish
        .mutate({
          workflowId: result.masterRender.workflowId,
          attempt: result.masterRender.attempt,
          status: 'failed',
          error: error instanceof Error ? error.message : String(error),
          ...completionSource,
        })
        .catch(reportError => {
          console.error('[modification-manager] failed to report master render failure', reportError)
        })
      throw error
    }

    if (renderOutcome.status === 'canceled') {
      this.emit({ type: 'canceled', sourceTimelineVersionId })
      return
    }

    await trpcClient.project.masterRender.finish.mutate({
      workflowId: result.masterRender.workflowId,
      attempt: result.masterRender.attempt,
      status: 'succeeded',
      ...completionSource,
    })
    if (renderAssetsAfterMaster) {
      for (const assetRender of assetRenders) {
        await renderManager.register({
          deliverableId: renderAssetsAfterMaster.deliverableId,
          stageName: 'Distribution',
          ...assetRender,
        })
      }
    }
    this.emit({ type: 'created', sourceTimelineVersionId })
  }

  private async runTimelineVersionCreation({
    masters,
    note,
    stageName,
    syncCommentsEnabled,
    sourceTimelineVersionAssetId,
    sourceTimelineVersionId,
  }: ModificationManagerInput) {
    const result = await trpcClient.project.timelineVersion.create.mutate({
      sourceTimelineVersionId,
      stageName,
      note,
      platformGroups: masters,
    })
    const hasSourceTimeline = result.sourceTimelineName.trim().length > 0

    if (hasSourceTimeline) {
      const bridgeComments =
        syncCommentsEnabled && sourceTimelineVersionAssetId
          ? toBridgeSyncComments(
              (
                await trpcClient.project.comments.list.query({
                  timelineVersionAssetId: sourceTimelineVersionAssetId,
                  allComments: true,
                })
              ).items,
            )
          : null

      await renderManager.runExclusive(async () => {
        for (const modification of result.modifications) {
          await bridge.duplicateTimeline({
            project_name: result.davinciProjectName,
            project_manager_path: result.projectManagerPath,
            source_timeline_name: result.sourceTimelineName,
            new_timeline_name: modification.newTimelineName,
          })
          if (bridgeComments) {
            await bridge.syncComments({
              project_name: result.davinciProjectName,
              project_manager_path: result.projectManagerPath,
              timeline_name: modification.newTimelineName,
              comment_list: bridgeComments,
              sync_mode: 'replace',
            })
          }
        }
      })
    }

    this.emit({ type: 'created', sourceTimelineVersionId })
  }
}

export const modificationManager = new ModificationManager()
